import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Wallet, Zap, ArrowUpRight, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Reveal } from "@/components/Reveal";
import { toast } from "sonner";

const PERKS = [
  { icon: Zap, t: "List in minutes", d: "Title, photos, price, delivery time — done. The simplest upload flow, made for makers." },
  { icon: Wallet, t: "Keep more, always", d: "Zero listing fees. Zero subscription. We only take a small 5–10% commission when you sell." },
  { icon: Sparkles, t: "Reach India", d: "Get discovered by buyers browsing by recipient, occasion and festival across the country." },
];

export default function SellOnHGK() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ specialty: "", bio: "", gst_number: "", bank_account: "", bank_ifsc: "", phone: "" });

  if (user && (user.role === "seller" || user.role === "admin")) return <Navigate to="/seller" replace />;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onboard = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/seller/onboard", form);
      toast.success("Welcome aboard! Your shop is live.");
      window.location.href = "/seller";
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not complete onboarding");
      setLoading(false);
    }
  };

  return (
    <div className="pt-24 sm:pt-32 pb-24">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="overline text-clay mb-5">Sell on handmadegiftkart</div>
            <h1 className="font-serif text-5xl sm:text-7xl tracking-tighter leading-[0.95]">
              Your craft<br /> deserves a <span className="italic text-clay">shop.</span>
            </h1>
            <p className="text-mute mt-6 max-w-md leading-relaxed">
              Join independent makers across India. Free unlimited listings, honest commission, and buyers who value handmade.
            </p>
          </div>
          <div className="grid gap-4">
            {PERKS.map((p, i) => (
              <Reveal key={p.t} delay={i * 0.08}>
                <div className="flex gap-4 bg-surface border border-line rounded-sm p-5">
                  <div className="w-11 h-11 rounded-full bg-clay/10 grid place-items-center shrink-0"><p.icon className="w-5 h-5 text-clay" /></div>
                  <div>
                    <h3 className="font-serif text-xl">{p.t}</h3>
                    <p className="text-sm text-mute mt-1 leading-relaxed">{p.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* Onboarding */}
        <div className="mt-20 bg-forest text-sand rounded-sm p-6 sm:p-12">
          {!user ? (
            <div className="text-center max-w-lg mx-auto py-6">
              <h2 className="font-serif text-3xl sm:text-4xl tracking-tight">Ready to open your shop?</h2>
              <p className="text-sand/70 mt-3">Create a seller account in seconds, then add your first product.</p>
              <div className="flex flex-wrap gap-3 justify-center mt-8">
                <Link to="/register" className="inline-flex items-center gap-2 rounded-full bg-clay text-sand px-7 py-3.5 text-sm hover:bg-terra transition-colors" data-testid="sell-register-btn">
                  Create seller account <ArrowUpRight className="w-4 h-4" />
                </Link>
                <Link to="/login" className="inline-flex items-center rounded-full border border-sand/40 px-7 py-3.5 text-sm hover:bg-sand/10 transition-colors">I already have an account</Link>
              </div>
            </div>
          ) : (
            <motion.form initial={{ opacity: 0 }} animate={{ opacity: 1 }} onSubmit={onboard} className="max-w-2xl mx-auto">
              <h2 className="font-serif text-3xl tracking-tight mb-2">Complete your maker profile</h2>
              <p className="text-sand/70 text-sm mb-6">GST & bank details are required for tax compliance and payouts.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="overline text-terra block mb-1.5">Specialty</label>
                  <input required value={form.specialty} onChange={set("specialty")} placeholder="e.g. Resin art & keepsakes"
                    className="w-full bg-sand text-ink border border-line rounded-sm px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-terra" data-testid="onboard-specialty" />
                </div>
                <div className="sm:col-span-2">
                  <label className="overline text-terra block mb-1.5">About your craft</label>
                  <textarea required value={form.bio} onChange={set("bio")} rows={3} placeholder="Tell buyers your story…"
                    className="w-full bg-sand text-ink border border-line rounded-sm px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-terra resize-none" data-testid="onboard-bio" />
                </div>
                <div>
                  <label className="overline text-terra block mb-1.5">GST number</label>
                  <input required value={form.gst_number} onChange={set("gst_number")} placeholder="27AAAAA0000A1Z5"
                    className="w-full bg-sand text-ink border border-line rounded-sm px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-terra" data-testid="onboard-gst" />
                </div>
                <div>
                  <label className="overline text-terra block mb-1.5">Phone</label>
                  <input required value={form.phone} onChange={set("phone")}
                    className="w-full bg-sand text-ink border border-line rounded-sm px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-terra" data-testid="onboard-phone" />
                </div>
                <div>
                  <label className="overline text-terra block mb-1.5">Bank account no.</label>
                  <input required value={form.bank_account} onChange={set("bank_account")}
                    className="w-full bg-sand text-ink border border-line rounded-sm px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-terra" data-testid="onboard-bank" />
                </div>
                <div>
                  <label className="overline text-terra block mb-1.5">IFSC code</label>
                  <input required value={form.bank_ifsc} onChange={set("bank_ifsc")}
                    className="w-full bg-sand text-ink border border-line rounded-sm px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-terra" data-testid="onboard-ifsc" />
                </div>
              </div>
              <button type="submit" disabled={loading} className="mt-6 inline-flex items-center gap-2 rounded-full bg-clay text-sand px-8 py-3.5 text-sm hover:bg-terra transition-colors disabled:opacity-60" data-testid="onboard-submit">
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Setting up…</> : "Open my shop"}
              </button>
            </motion.form>
          )}
        </div>
      </div>
    </div>
  );
}
